import produce from 'immer';

import {Measurement, MeasurementUnitFragment} from 'constants/MeasurementConstants';
import {readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {Pagination} from 'lib/Listings';
import {BaseTypedValueType, Cell} from 'notebook/tables/TableConstants';
import {
  PlateMap,
  PlateMaterialInput,
  ReagentAssignmentGroup,
  ReagentType,
} from 'samples/plate-maps/PlateMapConstants';
import {getFlattenedWellIndex} from 'samples/plate-maps/PlateMapUtil';
import {
  getDetailViewRow,
  getDetailViewRowArgs,
} from 'samples/plate-maps/transfer-table/DetailTransferTableUtil';
import {
  FillPlateTransferTableViewMode,
  ReagentMeasurementCellsByWells,
  WellViewRowContents,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {
  assignMaterialInputsToWells,
  coerceNumericValue,
  getMaterialInputId,
  isChangedMaterialInput,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableUtil';
import {getDefaultMeasurementValidationErrors} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';

/**
 * Gets the coerced Measurement value from the contents of the value and unit table cells.
 */
function getMeasurementFromCells(valueCell: Cell, unitCell: Cell): Measurement {
  let unit = null;
  if (unitCell.typedValue != null && unitCell.typedValue.type === BaseTypedValueType.UNIT_REFERENCE) {
    unit = readFragmentFromCache(MeasurementUnitFragment, unitCell.typedValue.unitSummary.id);
  }

  return {
    value: coerceNumericValue(valueCell),
    unit,
  };
}

/**
 * Updates the reagents in the plate map wells with the given measurement values.
 * @param plateMap The plate map to update
 * @param reagentMeasurementCellsByWell A mapping from plate map well index to reagents to measurement values
 */
export function updateReagentMeasurementCellsByWells(
  plateMap: PlateMap,
  reagentMeasurementCellsByWell: ReagentMeasurementCellsByWells
): PlateMap {
  const changedMaterialInputsByEntityId: {[entityId: string]: Set<string>} = {};
  const updatedPlateMap = produce(plateMap, (draft) => {
    for (const well of draft.wells) {
      const wellIndex = getFlattenedWellIndex(well.position.row, well.position.column, draft.size);
      // If we don't have any sources or contents in a well, we don't bother making a row for the well in the
      // table, so there's nothing to update here.
      if (reagentMeasurementCellsByWell[wellIndex] == null) {
        continue;
      }
      const {contentCells, sourceCells, finalQuantity, finalQuantityUnit} =
        reagentMeasurementCellsByWell[wellIndex];
      well.finalQuantity = {
        measurement: getMeasurementFromCells(finalQuantity, finalQuantityUnit),
        errors: getDefaultMeasurementValidationErrors(),
      };
      for (const content of well.contents) {
        const {finalConcentration, finalConcentrationUnit, materialInput} = contentCells[content.id];
        content.finalConcentration = {
          measurement: getMeasurementFromCells(finalConcentration, finalConcentrationUnit),
          errors: getDefaultMeasurementValidationErrors(),
        };
        const materialInputId = getMaterialInputId(materialInput);
        if (materialInputId != null) {
          if (isChangedMaterialInput(materialInputId, content)) {
            changedMaterialInputsByEntityId[content.id] ??= new Set();
            changedMaterialInputsByEntityId[content.id].add(materialInputId);
          }
          content.materialInput = {materialInputId, error: null};
        }
      }
      for (const source of well.sources) {
        const {contents, transferQuantity, transferQuantityUnit} = sourceCells[source.id];
        source.transferQuantity = {
          measurement: getMeasurementFromCells(transferQuantity, transferQuantityUnit),
          errors: getDefaultMeasurementValidationErrors(),
        };
        if (source.type === ReagentType.SOURCE_CONTAINABLE) {
          // Not a true 'content', but much easier to have the same shape for both types of sources
          const {
            finalConcentration,
            finalConcentrationUnit,
            materialInput,
            sourceConcentration,
            sourceConcentrationUnit,
          } = contents[source.id];
          source.finalConcentration = {
            measurement: getMeasurementFromCells(finalConcentration, finalConcentrationUnit),
            errors: getDefaultMeasurementValidationErrors(),
          };
          source.sourceConcentration = {
            measurement: getMeasurementFromCells(sourceConcentration, sourceConcentrationUnit),
            errors: getDefaultMeasurementValidationErrors(),
          };
          const materialInputId = getMaterialInputId(materialInput);
          if (materialInputId != null) {
            if (isChangedMaterialInput(materialInputId, source)) {
              changedMaterialInputsByEntityId[source.id] ??= new Set();
              changedMaterialInputsByEntityId[source.id].add(materialInputId);
            }
            source.materialInput = {materialInputId, error: null};
          }
        } else {
          for (const content of source.contents) {
            const {finalConcentration, finalConcentrationUnit, materialInput} = contents[content.id];
            content.finalConcentration = {
              measurement: getMeasurementFromCells(finalConcentration, finalConcentrationUnit),
              errors: getDefaultMeasurementValidationErrors(),
            };
            const materialInputId = getMaterialInputId(materialInput);
            if (materialInputId != null) {
              if (isChangedMaterialInput(materialInputId, content)) {
                changedMaterialInputsByEntityId[content.id] ??= new Set();
                changedMaterialInputsByEntityId[content.id].add(materialInputId);
              }
              content.materialInput = {materialInputId, error: null};
            }
          }
        }
      }
    }
  });

  return assignMaterialInputsToWells(updatedPlateMap, changedMaterialInputsByEntityId);
}

/**
 * Gets what the value of each cell in each row should be in detail views (Well View, Plate Record).
 * @param pagination Information about how to page through the selected wells
 * @param plateMap The plate map representing the plate being filled
 * @param plateMaterialInputs In a procedure context, a list of plate-used material inputs.
 * @param reagentGroupsById A mapping of reagent group ids to reagent groups
 * @param selectedWellIndices What indices in the plate are currently selected
 * @param showMaterialInputColumn Whether to include a column for configuring material input associations
 */
export function getWellViewRowsContents({
  pagination,
  plateMap,
  plateMaterialInputs,
  reagentGroupsById,
  selectedWellIndices,
  showMaterialInputColumn,
}: {
  pagination: Pagination;
  plateMap: PlateMap;
  plateMaterialInputs?: Array<PlateMaterialInput>;
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  selectedWellIndices: Set<number>;
  showMaterialInputColumn: boolean;
}): Array<WellViewRowContents> {
  return getDetailViewRowArgs({
    pagination,
    plateMap,
    plateMaterialInputs,
    reagentGroupsById,
    selectedWellIndices,
    showMaterialInputColumn,
    viewMode: FillPlateTransferTableViewMode.WELL,
  }).map((args) => getDetailViewRow(args));
}
